<template>
  <div>
    <div v-if="!pageLoading">
      <v-card flat class="tw-mt-5 md:tw-mt-16">
        <div class="tw-px-4 md:tw-px-8">
          <!-- Sophisticated Header with Gradient -->
          <div class="tw-relative tw-overflow-hidden tw-rounded-xl tw-mb-8"
               style="background: linear-gradient(135deg, #1976d2 0%, #1565c0 50%, #0d47a1 100%);">
            <div class="tw-absolute tw-inset-0 tw-bg-black tw-opacity-10"></div>
            <div class="tw-relative tw-z-10 tw-text-center tw-py-12 tw-px-6">
              <div class="tw-mb-4">
                <v-icon size="64" color="white" class="tw-mb-2">mdi-shield-airplane</v-icon>
              </div>
              <h1 class="tw-text-white tw-text-3xl md:tw-text-4xl tw-font-bold tw-mb-2">
                Complete Your Travel Insurance Purchase
              </h1>
              <p class="tw-text-blue-100 tw-text-lg">
                Comprehensive protection for your journey abroad
              </p>
            </div>
          </div>

          <!-- Premium Display Card with Advanced Styling -->
          <v-card class="tw-mb-8 tw-overflow-hidden" elevation="8"
                  style="background: linear-gradient(135deg, #f8f9fa 0%, #e3f2fd 100%);">
            <v-card-text class="tw-p-0">
              <div class="tw-bg-gradient-to-r tw-from-green-500 tw-to-emerald-600 tw-p-6">
                <div class="tw-text-center">
                  <h2 class="tw-text-white tw-text-2xl tw-font-bold tw-mb-2">
                    Premium Summary
                  </h2>
                  <div class="tw-flex tw-justify-center tw-space-x-2">
                    <v-chip color="white" variant="elevated" size="small">
                      <v-icon start size="16">mdi-earth</v-icon>
                      Non-Resident Protection
                    </v-chip>
                    <v-chip color="white" variant="elevated" size="small" v-if="quote">
                      <v-icon start size="16">mdi-airplane-takeoff</v-icon>
                      {{ quote.destination || 'International' }}
                    </v-chip>
                  </div>
                </div>
              </div>

              <div class="tw-p-8">
                <v-row align="center" justify="center" class="tw-mb-6">
                  <v-col cols="12" sm="5" class="tw-text-center">
                    <div class="tw-bg-white tw-rounded-xl tw-p-6 tw-shadow-lg tw-border tw-border-gray-100">
                      <v-icon size="32" color="success" class="tw-mb-3">mdi-cash-multiple</v-icon>
                      <p class="tw-text-gray-600 tw-text-sm tw-font-medium tw-mb-1">USD Premium</p>
                      <p class="tw-text-3xl tw-font-bold tw-text-gray-800">
                        ${{ quote ? quote.premium : 0 }}
                      </p>
                    </div>
                  </v-col>

                  <v-col cols="12" sm="2" class="tw-text-center tw-hidden sm:tw-block">
                    <v-icon size="40" color="primary">mdi-arrow-right</v-icon>
                  </v-col>

                  <v-col cols="12" sm="5" class="tw-text-center">
                    <div class="tw-bg-white tw-rounded-xl tw-p-6 tw-shadow-lg tw-border tw-border-blue-200 tw-border-2">
                      <v-icon size="32" color="primary" class="tw-mb-3">mdi-currency-usd</v-icon>
                      <p class="tw-text-gray-600 tw-text-sm tw-font-medium tw-mb-1">KES Amount</p>
                      <p class="tw-text-3xl tw-font-bold tw-text-blue-700">
                        KES {{ formatNumber(kshAmount) }}
                      </p>
                      <p class="tw-text-xs tw-text-gray-500 tw-mt-1">
                        Rate: 1 USD = {{ exchangeRate }} KES
                      </p>
                    </div>
                  </v-col>
                </v-row>
              </div>
            </v-card-text>
          </v-card>

          <!-- Sophisticated Quote Summary Cards -->
          <v-row class="tw-mb-8">
            <!-- Travel Details Card with Modern Design -->
            <v-col cols="12" md="6">
              <v-card class="tw-h-full tw-overflow-hidden" elevation="8"
                      style="background: linear-gradient(135deg, #f8f9fa 0%, #e8f4fd 100%);">
                <!-- Card Header with Gradient -->
                <div class="tw-bg-gradient-to-r tw-from-blue-500 tw-to-indigo-600 tw-p-6">
                  <div class="tw-flex tw-items-center tw-text-white">
                    <div class="tw-bg-white tw-bg-opacity-20 tw-rounded-full tw-p-3 tw-mr-4">
                      <v-icon size="32" color="white">mdi-airplane-takeoff</v-icon>
                    </div>
                    <div>
                      <h3 class="tw-text-xl tw-font-bold tw-mb-1">Travel Details</h3>
                      <p class="tw-text-blue-100 tw-text-sm">Your journey information</p>
                    </div>
                  </div>
                </div>

                <!-- Card Content -->
                <v-card-text class="tw-p-8" v-if="quote">
                  <div class="tw-space-y-6">
                    <div v-if="quote.destination" class="tw-group">
                      <div class="tw-bg-white tw-rounded-xl tw-p-4 tw-shadow-sm tw-border tw-border-gray-100 hover:tw-shadow-md tw-transition-all">
                        <div class="tw-flex tw-items-center">
                          <div class="tw-bg-green-50 tw-rounded-full tw-p-2 tw-mr-4">
                            <v-icon size="20" color="success">mdi-map-marker</v-icon>
                          </div>
                          <div>
                            <p class="tw-text-sm tw-text-gray-600 tw-font-medium tw-mb-1">Destination</p>
                            <p class="tw-text-lg tw-font-bold tw-text-gray-800">{{ quote.destination }}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div v-if="quote.travel_type" class="tw-group">
                      <div class="tw-bg-white tw-rounded-xl tw-p-4 tw-shadow-sm tw-border tw-border-gray-100 hover:tw-shadow-md tw-transition-all">
                        <div class="tw-flex tw-items-center">
                          <div class="tw-bg-blue-50 tw-rounded-full tw-p-2 tw-mr-4">
                            <v-icon size="20" color="primary">mdi-airplane</v-icon>
                          </div>
                          <div>
                            <p class="tw-text-sm tw-text-gray-600 tw-font-medium tw-mb-1">Travel Type</p>
                            <p class="tw-text-lg tw-font-bold tw-text-gray-800">{{ quote.travel_type | capitalize }}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div v-if="quote.travel_start_date && quote.travel_end_date" class="tw-group">
                      <div class="tw-bg-white tw-rounded-xl tw-p-4 tw-shadow-sm tw-border tw-border-gray-100 hover:tw-shadow-md tw-transition-all">
                        <div class="tw-flex tw-items-center">
                          <div class="tw-bg-purple-50 tw-rounded-full tw-p-2 tw-mr-4">
                            <v-icon size="20" color="purple">mdi-calendar-range</v-icon>
                          </div>
                          <div>
                            <p class="tw-text-sm tw-text-gray-600 tw-font-medium tw-mb-1">Travel Period</p>
                            <p class="tw-text-lg tw-font-bold tw-text-gray-800">{{ formatDateRange(quote.travel_start_date, quote.travel_end_date) }}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div v-if="quote.number_of_travelers" class="tw-group">
                      <div class="tw-bg-white tw-rounded-xl tw-p-4 tw-shadow-sm tw-border tw-border-gray-100 hover:tw-shadow-md tw-transition-all">
                        <div class="tw-flex tw-items-center">
                          <div class="tw-bg-orange-50 tw-rounded-full tw-p-2 tw-mr-4">
                            <v-icon size="20" color="orange">mdi-account-group</v-icon>
                          </div>
                          <div>
                            <p class="tw-text-sm tw-text-gray-600 tw-font-medium tw-mb-1">Number of Travelers</p>
                            <p class="tw-text-lg tw-font-bold tw-text-gray-800">{{ quote.number_of_travelers }}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Coverage Benefits Card with Advanced Design -->
            <v-col cols="12" md="6">
              <v-card class="tw-h-full tw-overflow-hidden" elevation="8"
                      style="background: linear-gradient(135deg, #f8f9fa 0%, #fff0f3 100%);">
                <!-- Card Header with Gradient -->
                <div class="tw-bg-gradient-to-r tw-from-emerald-500 tw-to-teal-600 tw-p-6">
                  <div class="tw-flex tw-items-center tw-text-white">
                    <div class="tw-bg-white tw-bg-opacity-20 tw-rounded-full tw-p-3 tw-mr-4">
                      <v-icon size="32" color="white">mdi-shield-check</v-icon>
                    </div>
                    <div>
                      <h3 class="tw-text-xl tw-font-bold tw-mb-1">Coverage Benefits</h3>
                      <p class="tw-text-emerald-100 tw-text-sm">Comprehensive protection</p>
                    </div>
                  </div>
                </div>

                <!-- Card Content -->
                <v-card-text class="tw-p-8" v-if="quote">
                  <div class="tw-space-y-4">
                    <!-- Medical Expenses -->
                    <div class="tw-bg-gradient-to-r tw-from-green-50 tw-to-emerald-50 tw-rounded-xl tw-p-5 tw-border tw-border-green-200 hover:tw-shadow-lg tw-transition-all">
                      <div class="tw-flex tw-items-center tw-justify-between">
                        <div class="tw-flex tw-items-center">
                          <div class="tw-bg-green-500 tw-rounded-full tw-p-2 tw-mr-4">
                            <v-icon size="20" color="white">mdi-hospital-box</v-icon>
                          </div>
                          <div>
                            <p class="tw-text-sm tw-text-green-700 tw-font-medium tw-mb-1">Medical Expenses</p>
                            <p class="tw-text-xl tw-font-bold tw-text-green-800">USD {{ quote.coverage_amount | formatNum }}</p>
                          </div>
                        </div>
                        <v-chip color="success" variant="elevated" size="small">
                          <v-icon start size="14">mdi-check</v-icon>
                          Covered
                        </v-chip>
                      </div>
                    </div>

                    <!-- Trip Cancellation -->
                    <div class="tw-bg-gradient-to-r tw-from-amber-50 tw-to-yellow-50 tw-rounded-xl tw-p-5 tw-border tw-border-amber-200 hover:tw-shadow-lg tw-transition-all">
                      <div class="tw-flex tw-items-center tw-justify-between">
                        <div class="tw-flex tw-items-center">
                          <div class="tw-bg-amber-500 tw-rounded-full tw-p-2 tw-mr-4">
                            <v-icon size="20" color="white">mdi-calendar-remove</v-icon>
                          </div>
                          <div>
                            <p class="tw-text-sm tw-text-amber-700 tw-font-medium tw-mb-1">Trip Cancellation</p>
                            <p class="tw-text-xl tw-font-bold tw-text-amber-800">USD {{ (quote.coverage_amount * 0.8) | formatNum }}</p>
                          </div>
                        </div>
                        <v-chip color="warning" variant="elevated" size="small">
                          <v-icon start size="14">mdi-check</v-icon>
                          Covered
                        </v-chip>
                      </div>
                    </div>

                    <!-- Luggage Protection -->
                    <div class="tw-bg-gradient-to-r tw-from-blue-50 tw-to-cyan-50 tw-rounded-xl tw-p-5 tw-border tw-border-blue-200 hover:tw-shadow-lg tw-transition-all">
                      <div class="tw-flex tw-items-center tw-justify-between">
                        <div class="tw-flex tw-items-center">
                          <div class="tw-bg-blue-500 tw-rounded-full tw-p-2 tw-mr-4">
                            <v-icon size="20" color="white">mdi-bag-suitcase</v-icon>
                          </div>
                          <div>
                            <p class="tw-text-sm tw-text-blue-700 tw-font-medium tw-mb-1">Luggage Protection</p>
                            <p class="tw-text-xl tw-font-bold tw-text-blue-800">USD {{ (quote.coverage_amount * 0.1) | formatNum }}</p>
                          </div>
                        </div>
                        <v-chip color="info" variant="elevated" size="small">
                          <v-icon start size="14">mdi-check</v-icon>
                          Covered
                        </v-chip>
                      </div>
                    </div>

                    <!-- Emergency Evacuation -->
                    <div class="tw-bg-gradient-to-r tw-from-red-50 tw-to-pink-50 tw-rounded-xl tw-p-5 tw-border tw-border-red-200 hover:tw-shadow-lg tw-transition-all">
                      <div class="tw-flex tw-items-center tw-justify-between">
                        <div class="tw-flex tw-items-center">
                          <div class="tw-bg-red-500 tw-rounded-full tw-p-2 tw-mr-4">
                            <v-icon size="20" color="white">mdi-helicopter</v-icon>
                          </div>
                          <div>
                            <p class="tw-text-sm tw-text-red-700 tw-font-medium tw-mb-1">Emergency Evacuation</p>
                            <p class="tw-text-xl tw-font-bold tw-text-red-800">USD {{ 1000000 | formatNum }}</p>
                          </div>
                        </div>
                        <v-chip color="error" variant="elevated" size="small">
                          <v-icon start size="14">mdi-check</v-icon>
                          Covered
                        </v-chip>
                      </div>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <div class="tw-px-4 md:tw-px-12 tw-mt-8 md:tw-mt-12">
            <!-- Modern Customer Details Section -->
            <div v-if="formData" class="tw-w-full tw-mb-10">
              <div class="tw-mb-6">
                <h2 class="tw-text-2xl tw-font-bold tw-text-gray-800 tw-mb-2">
                  <v-icon size="28" color="primary" class="tw-mr-3">mdi-account</v-icon>
                  Customer Information
                </h2>
                <p class="tw-text-gray-600">Your personal details for this policy</p>
              </div>

              <v-card class="tw-mb-8 tw-overflow-hidden" elevation="8"
                      style="background: linear-gradient(135deg, #f8f9fa 0%, #f0f7ff 100%);">
                <!-- Modern Header -->
                <div class="tw-bg-gradient-to-r tw-from-indigo-500 tw-to-purple-600 tw-p-6">
                  <div class="tw-flex tw-items-center tw-text-white">
                    <div class="tw-bg-white tw-bg-opacity-20 tw-rounded-full tw-p-3 tw-mr-4">
                      <v-icon size="28" color="white">mdi-account-circle</v-icon>
                    </div>
                    <div>
                      <h3 class="tw-text-xl tw-font-bold tw-mb-1">Personal Details</h3>
                      <p class="tw-text-indigo-100 tw-text-sm">Customer information for policy issuance</p>
                    </div>
                  </div>
                </div>

                <v-card-text class="tw-p-8">
                  <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-6">
                    <div class="tw-space-y-4">
                      <div v-if="formData.full_name">
                        <div class="tw-bg-white tw-rounded-xl tw-p-4 tw-shadow-sm tw-border tw-border-gray-100 hover:tw-shadow-md tw-transition-all">
                          <div class="tw-flex tw-items-center">
                            <div class="tw-bg-blue-50 tw-rounded-full tw-p-2 tw-mr-4">
                              <v-icon size="20" color="primary">mdi-account</v-icon>
                            </div>
                            <div>
                              <div class="tw-text-sm tw-text-gray-600 tw-font-medium tw-mb-1">Full Name</div>
                              <div class="tw-text-lg tw-font-bold tw-text-gray-800">{{ formData.full_name }}</div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div v-if="formData.email">
                        <div class="tw-bg-white tw-rounded-xl tw-p-4 tw-shadow-sm tw-border tw-border-gray-100 hover:tw-shadow-md tw-transition-all">
                          <div class="tw-flex tw-items-center">
                            <div class="tw-bg-green-50 tw-rounded-full tw-p-2 tw-mr-4">
                              <v-icon size="20" color="success">mdi-email</v-icon>
                            </div>
                            <div>
                              <div class="tw-text-sm tw-text-gray-600 tw-font-medium tw-mb-1">Email</div>
                              <div class="tw-text-lg tw-font-bold tw-text-gray-800">{{ formData.email }}</div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div v-if="formData.mobile_main">
                        <div class="tw-bg-white tw-rounded-xl tw-p-4 tw-shadow-sm tw-border tw-border-gray-100 hover:tw-shadow-md tw-transition-all">
                          <div class="tw-flex tw-items-center">
                            <div class="tw-bg-orange-50 tw-rounded-full tw-p-2 tw-mr-4">
                              <v-icon size="20" color="orange">mdi-phone</v-icon>
                            </div>
                            <div>
                              <div class="tw-text-sm tw-text-gray-600 tw-font-medium tw-mb-1">Phone Number</div>
                              <div class="tw-text-lg tw-font-bold tw-text-gray-800">{{ formData.mobile_main }}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="tw-space-y-4">
                      <div v-if="formData.passport_no">
                        <div class="tw-bg-white tw-rounded-xl tw-p-4 tw-shadow-sm tw-border tw-border-gray-100 hover:tw-shadow-md tw-transition-all">
                          <div class="tw-flex tw-items-center">
                            <div class="tw-bg-purple-50 tw-rounded-full tw-p-2 tw-mr-4">
                              <v-icon size="20" color="purple">mdi-passport</v-icon>
                            </div>
                            <div>
                              <div class="tw-text-sm tw-text-gray-600 tw-font-medium tw-mb-1">Passport Number</div>
                              <div class="tw-text-lg tw-font-bold tw-text-gray-800">{{ formData.passport_no }}</div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div v-if="formData.date_of_birth">
                        <div class="tw-bg-white tw-rounded-xl tw-p-4 tw-shadow-sm tw-border tw-border-gray-100 hover:tw-shadow-md tw-transition-all">
                          <div class="tw-flex tw-items-center">
                            <div class="tw-bg-pink-50 tw-rounded-full tw-p-2 tw-mr-4">
                              <v-icon size="20" color="pink">mdi-calendar</v-icon>
                            </div>
                            <div>
                              <div class="tw-text-sm tw-text-gray-600 tw-font-medium tw-mb-1">Date of Birth</div>
                              <div class="tw-text-lg tw-font-bold tw-text-gray-800">{{ formatDate(formData.date_of_birth) }}</div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div v-if="formData.residency">
                        <div class="tw-bg-white tw-rounded-xl tw-p-4 tw-shadow-sm tw-border tw-border-gray-100 hover:tw-shadow-md tw-transition-all">
                          <div class="tw-flex tw-items-center">
                            <div class="tw-bg-cyan-50 tw-rounded-full tw-p-2 tw-mr-4">
                              <v-icon size="20" color="cyan">mdi-earth</v-icon>
                            </div>
                            <div>
                              <div class="tw-text-sm tw-text-gray-600 tw-font-medium tw-mb-1">Residency</div>
                              <div class="tw-text-lg tw-font-bold tw-text-gray-800">{{ formData.residency }}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </div>

            <!-- Modern Payment Section -->
            <div class="tw-w-full tw-mb-10">
              <div class="tw-mb-6">
                <h2 class="tw-text-2xl tw-font-bold tw-text-gray-800 tw-mb-2">
                  <v-icon size="28" color="primary" class="tw-mr-3">mdi-credit-card</v-icon>
                  Payment Details
                </h2>
                <p class="tw-text-gray-600">Complete your payment to activate your travel insurance policy</p>
              </div>

              <v-alert
                v-if="paymentError"
                type="error"
                variant="elevated"
                class="tw-mb-6 tw-rounded-xl"
                @input="paymentError = null"
              >
                <div class="tw-font-bold tw-mb-1">Payment Error</div>
                {{ paymentError }}
              </v-alert>

              <v-alert
                v-if="paymentSuccess"
                type="success"
                variant="elevated"
                class="tw-mb-6 tw-rounded-xl"
              >
                <div class="tw-font-bold tw-mb-1">Payment Successful!</div>
                Your policy is being processed.
              </v-alert>

              <!-- Sophisticated Payment Amount Card -->
              <v-card class="tw-mb-8 tw-overflow-hidden" elevation="8"
                      style="background: linear-gradient(135deg, #f8f9fa 0%, #e8f5e8 100%);">
                <div class="tw-bg-gradient-to-r tw-from-emerald-500 tw-to-green-600 tw-p-6">
                  <div class="tw-text-center tw-text-white">
                    <div class="tw-bg-white tw-bg-opacity-20 tw-rounded-full tw-p-4 tw-inline-block tw-mb-4">
                      <v-icon size="40" color="white">mdi-cash-multiple</v-icon>
                    </div>
                    <h3 class="tw-text-xl tw-font-bold tw-mb-2">Payment Summary</h3>
                    <p class="tw-text-green-100 tw-text-sm">Secure payment processing</p>
                  </div>
                </div>

                <v-card-text class="tw-p-8 tw-text-center">
                  <div class="tw-bg-white tw-rounded-xl tw-p-6 tw-shadow-sm tw-border tw-border-gray-100 tw-max-w-md tw-mx-auto">
                    <p class="tw-text-lg tw-font-semibold tw-text-gray-700 tw-mb-3">Total Amount</p>
                    <div class="tw-text-4xl tw-font-bold tw-text-emerald-600 tw-mb-2">
                      KSH {{ formatAmount(kshAmount) }}
                    </div>
                    <div class="tw-text-sm tw-text-gray-600 tw-bg-gray-50 tw-rounded-lg tw-px-3 tw-py-2 tw-inline-block">
                      USD {{ quote ? quote.premium : 0 | formatNum }} @ {{ exchangeRate }} KES
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <!-- Sophisticated Payment Methods -->
              <div class="tw-mb-8">
                <div class="tw-text-center tw-mb-8">
                  <h3 class="tw-text-2xl tw-font-bold tw-text-gray-800 tw-mb-2">Choose Payment Method</h3>
                  <p class="tw-text-gray-600">Select your preferred payment option for secure processing</p>
                </div>

                <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-6">
                  <!-- M-Pesa Card -->
                  <v-card
                    class="tw-cursor-pointer tw-transition-all hover:tw-shadow-xl hover:tw--translate-y-1 tw-overflow-hidden"
                    :class="{ 'tw-ring-4 tw-ring-green-300 tw-shadow-xl': showMpesaInput }"
                    elevation="6"
                    @click="initiateMpesaPayment"
                    style="background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);"
                  >
                    <div class="tw-bg-gradient-to-r tw-from-green-500 tw-to-emerald-600 tw-p-4">
                      <div class="tw-text-center">
                        <div class="tw-bg-white tw-bg-opacity-20 tw-rounded-full tw-p-3 tw-inline-block tw-mb-2">
                          <v-icon size="40" color="white">mdi-phone</v-icon>
                        </div>
                        <h4 class="tw-text-white tw-text-lg tw-font-bold">M-Pesa</h4>
                        <p class="tw-text-green-100 tw-text-sm">Mobile Money Payment</p>
                      </div>
                    </div>

                    <v-card-text class="tw-p-6 tw-text-center">
                      <div class="tw-text-green-700 tw-text-sm tw-mb-4 tw-font-medium">Pay instantly with your mobile money</div>
                      <v-btn
                        variant="flat"
                        color="green"
                        :loading="processingMpesa"
                        :disabled="!isValidForPayment"
                        size="large"
                        block
                        class="tw-rounded-xl tw-font-bold"
                      >
                        <v-icon start>mdi-phone</v-icon>
                        Pay with M-Pesa
                      </v-btn>
                    </v-card-text>
                  </v-card>

                  <!-- Pay by Link Card -->
                  <v-card
                    class="tw-cursor-pointer tw-transition-all hover:tw-shadow-xl hover:tw--translate-y-1 tw-overflow-hidden"
                    elevation="6"
                    @click="createPayByLink"
                    style="background: linear-gradient(135deg, #fef3f2 0%, #fee2e2 100%);"
                  >
                    <div class="tw-bg-gradient-to-r tw-from-blue-500 tw-to-indigo-600 tw-p-4">
                      <div class="tw-text-center">
                        <div class="tw-bg-white tw-bg-opacity-20 tw-rounded-full tw-p-3 tw-inline-block tw-mb-2">
                          <v-icon size="40" color="white">mdi-credit-card</v-icon>
                        </div>
                        <h4 class="tw-text-white tw-text-lg tw-font-bold">Pay by Link</h4>
                        <p class="tw-text-blue-100 tw-text-sm">Online Payment Gateway</p>
                      </div>
                    </div>

                    <v-card-text class="tw-p-6 tw-text-center">
                      <div class="tw-text-blue-700 tw-text-sm tw-mb-4 tw-font-medium">Pay via secure online payment gateway</div>
                      <v-btn
                        variant="flat"
                        color="blue"
                        :loading="processingPayByLink"
                        :disabled="!isValidForPayment"
                        size="large"
                        block
                        class="tw-rounded-xl tw-font-bold"
                      >
                        <v-icon start>mdi-credit-card</v-icon>
                        Pay by Link
                      </v-btn>
                    </v-card-text>
                  </v-card>
                </div>
              </div>

              <!-- Modern M-Pesa Phone Input -->
              <v-expand-transition>
                <div v-if="showMpesaInput">
                  <v-card class="tw-mb-6 tw-overflow-hidden" elevation="8"
                          style="background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);">
                    <div class="tw-bg-gradient-to-r tw-from-green-500 tw-to-emerald-600 tw-p-6">
                      <div class="tw-flex tw-items-center tw-text-white">
                        <div class="tw-bg-white tw-bg-opacity-20 tw-rounded-full tw-p-3 tw-mr-4">
                          <v-icon size="28" color="white">mdi-phone</v-icon>
                        </div>
                        <div>
                          <h3 class="tw-text-xl tw-font-bold tw-mb-1">M-Pesa Payment Details</h3>
                          <p class="tw-text-green-100 tw-text-sm">Enter your M-Pesa details to proceed</p>
                        </div>
                      </div>
                    </div>

                    <v-card-text class="tw-p-8">
                      <div class="tw-bg-white tw-rounded-xl tw-p-6 tw-shadow-sm tw-border tw-border-green-200 tw-max-w-md tw-mx-auto">
                        <v-text-field
                          v-model="mpesaPhone"
                          label="M-Pesa Phone Number"
                          hint="Enter your M-Pesa registered phone number (e.g., 0712345678)"
                          persistent-hint
                          variant="outlined"
                          :rules="phoneRules"
                          prepend-inner-icon="mdi-phone"
                          color="success"
                          class="tw-mb-4"
                        ></v-text-field>
                        <v-btn
                          size="large"
                          color="success"
                          :loading="processingMpesa"
                          :disabled="!isValidMpesaPhone"
                          @click="processMpesaPayment"
                          block
                          class="tw-rounded-xl tw-font-bold"
                          variant="flat"
                        >
                          <v-icon start>mdi-send</v-icon>
                          Send M-Pesa Request
                        </v-btn>
                      </div>
                    </v-card-text>
                  </v-card>
                </div>
              </v-expand-transition>

              <!-- Payment Status -->
              <v-expand-transition>
                <div v-if="paymentStatus">
                  <v-alert
                    :type="paymentStatus.type"
                    variant="outlined"
                    class="tw-mt-4"
                  >
                    <div class="tw-font-bold tw-mb-1">Payment Status</div>
                    {{ paymentStatus.message }}
                  </v-alert>
                </div>
              </v-expand-transition>
          </div>
          </div>
        </div>
      </v-card>
    </div>
    <div v-else>
      <v-skeleton-loader type="card"></v-skeleton-loader>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import nonResidentAuth from '@/services/nonResidentAuth';
import exchangeRates from '@/config/exchangeRates';
import { TravelPolicyService } from '@/services/TravelPolicyService';
import { travelPaymentReference } from '@/utils/travelPaymentReference';

export default {
  name: 'NonResidentTravelBuyCover',

  filters: {
    formatNum(value) {
      if (!value) return '0';
      return Number(value).toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
    capitalize: (value) => {
      if (!value) return '';
      const stringValue = value.toString();
      return stringValue.charAt(0).toUpperCase() + stringValue.slice(1);
    },
  },

  data() {
    return {
      quote: null,
      formData: null,
      pageLoading: false,
      exchangeRate: exchangeRates.USD_TO_KSH, // Use configured exchange rate
      mpesaPhone: '',
      showMpesaInput: false,
      processingMpesa: false,
      processingPayByLink: false,
      paymentError: null,
      paymentSuccess: false,
      paymentStatus: null,
      phoneRules: [
        (v) => !!v || 'Phone number is required',
        (v) => /^(0|254|\+254)[17]\d{8}$/.test(v.replace(/[\s\-()]/g, '')) || 'Enter a valid Kenyan phone number',
      ],
    };
  },

  computed: {
    kshAmount() {
      if (!this.quote || !this.quote.premium) {
        // console.warn('NonResidentTravelBuyCover - No premium found in quote:', this.quote);
        return 0;
      }
      return Math.round(this.quote.premium * this.exchangeRate);
    },

    isValidForPayment() {
      return this.quote && this.quote.premium && this.quote.premium > 0 && this.formData;
    },

    isValidMpesaPhone() {
      return this.mpesaPhone && this.phoneRules.every((rule) => rule(this.mpesaPhone) === true);
    },
  },

  async mounted() {
    await this.loadData();
    await this.loadExchangeRate();
  },

  methods: {
    async loadData() {
      try {
        // Load quote and form data from Vuex store
        this.quote = this.$store.getters['rfq/getTravelQuoteResponse'];
        this.formData = this.$store.getters['rfq/getTravelFormData'];

        // console.log('NonResidentTravelBuyCover - Quote from store:', this.quote);
        // console.log('NonResidentTravelBuyCover - Form data from store:', this.formData);

        // If no data in store, try to fetch using quote ID from query
        if ((!this.quote || !this.quote.premium) && this.$route.query.q) {
          // console.log('Fetching quote by ID:', this.$route.query.q);
          try {
            const response = await axios.get(`/customer/digital-quotation/pacis-travel/quotes/${this.$route.query.q}`);
            this.quote = response.data;
            // console.log('NonResidentTravelBuyCover - Quote loaded from API:', this.quote);
          } catch (error) {
            // console.error('Error fetching quote from API:', error);
            this.paymentError = 'Failed to load quote data. Please try again.';
          }
        }

        // Set default M-Pesa phone from form data
        if (this.formData && this.formData.mobile_main) {
          this.mpesaPhone = this.formData.mobile_main;
        }
      } catch (error) {
        // console.error('Error loading data:', error);
        this.paymentError = 'Failed to load quote data. Please try again.';
      }
    },

    async loadExchangeRate() {
      try {
        // Load exchange rate from store or fetch from API
        const rate = this.$store.getters['utils/getExchangeRate'];
        if (rate) {
          this.exchangeRate = rate;
        } else {
          // Use configured exchange rate as fallback
          this.exchangeRate = exchangeRates.USD_TO_KSH;
        }
        // console.log('NonResidentTravelBuyCover - Using exchange rate:', this.exchangeRate);
      } catch (error) {
        // console.log('Using configured exchange rate:', exchangeRates.USD_TO_KSH);
        this.exchangeRate = exchangeRates.USD_TO_KSH;
      }
    },

    formatPlanName(planValue) {
      const planNames = {
        bronze: 'Bronze Plan',
        silver: 'Silver Plan',
        gold: 'Gold Plan',
        platinum: 'Platinum Plan',
      };
      return planNames[planValue] || planValue;
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB');
    },

    formatAmount(amount) {
      return Number(amount).toLocaleString('en-US');
    },

    formatNumber(amount) {
      return Number(amount).toLocaleString('en-US');
    },

    formatDateRange(startDate, endDate) {
      if (!startDate || !endDate) return 'N/A';

      try {
        const start = new Date(startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        const end = new Date(endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        return `${start} - ${end}`;
      } catch (error) {
        return `${startDate} - ${endDate}`;
      }
    },

    calculateDays(startDate, endDate) {
      if (!startDate || !endDate) return 0;
      const start = new Date(startDate);
      const end = new Date(endDate);
      const diffTime = Math.abs(end - start);
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    },

    initiateMpesaPayment() {
      this.showMpesaInput = true;
      this.paymentError = null;
      this.paymentStatus = null;
    },

    async processMpesaPayment() {
      if (!this.isValidMpesaPhone) {
        this.paymentError = 'Please enter a valid M-Pesa phone number';
        return;
      }

      this.processingMpesa = true;
      this.paymentError = null;

      try {
        // Get authentication headers for API calls
        const headers = await nonResidentAuth.getAuthHeaders();

        // Format phone number for M-Pesa
        const formattedPhone = this.validatePhoneNumber(this.mpesaPhone);
        if (!formattedPhone) {
          throw new Error('Invalid phone number format');
        }

        // Initiate STK Push - exact format as specified
        const stkPayload = {
          transactionReference: travelPaymentReference(this.form?.passport_number || this.form?.passport_no || this.quote?.passport_no),
          transactionDescription: `TV_rfq-${this.quote.id}`,
          customerPhoneNumber: formattedPhone,
          payableAmount: parseInt(this.kshAmount, 10),
          serviceType: 'TRAVEL',
          transactionType: 'PAYMENT',
        };

        // console.log('NonResidentTravelBuyCover - M-Pesa STK payload:', JSON.stringify(stkPayload));
        // console.log('NonResidentTravelBuyCover - Expected format: {"transactionReference":"TRAVEL-241","transactionDescription":"Travel Insurance - 241","customerPhoneNumber":"254729680068","payableAmount":2322,"serviceType":"TRAVEL","transactionType":"PAYMENT"}');
        // console.log('NonResidentTravelBuyCover - Payload validation:', {
        //   hasTransactionReference: !!stkPayload.transactionReference,
        //   hasTransactionDescription: !!stkPayload.transactionDescription,
        //   hasCustomerPhoneNumber: !!stkPayload.customerPhoneNumber,
        //   payableAmountType: typeof stkPayload.payableAmount,
        //   payableAmountValue: stkPayload.payableAmount,
        //   hasServiceType: !!stkPayload.serviceType,
        //   hasTransactionType: !!stkPayload.transactionType,
        // });

        const response = await axios.post(
          'https://prod.api.pacisinsurance.com/customer/payment/client-api/v1/payments/mpesa/stk/request',
          stkPayload,
          { headers },
        );

        if (response.data && response.data.data) {
          this.paymentStatus = {
            type: 'info',
            message: 'M-Pesa payment request sent to your phone. Please complete the payment on your phone.',
          };

          // Start polling for payment status
          this.pollPaymentStatus(response.data.data.paymentId);
        } else {
          throw new Error('Failed to initiate M-Pesa payment');
        }
      } catch (error) {
        // console.error('M-Pesa payment error:', error);
        this.paymentError = 'Failed to initiate M-Pesa payment. Please try again.';
      } finally {
        this.processingMpesa = false;
      }
    },

    async createPayByLink() {
      this.processingPayByLink = true;
      this.paymentError = null;

      try {
        // Get authentication headers
        const headers = await nonResidentAuth.getAuthHeaders();

        const payload = {
          transactionReference: travelPaymentReference(this.form?.passport_number || this.form?.passport_no || this.quote?.passport_no),
          transactionDescription: `TV_rfq- ${this.quote.id}`,
          payableAmount: this.kshAmount,
          customerEmail: this.formData.email,
          customerPhone: this.formData.mobile_main,
        };

        const response = await axios.post(
          'https://prod.api.pacisinsurance.com/3rdparty/payment/pay-by-link',
          payload,
          { headers },
        );

        if (response.data && response.data.paymentLink) {
          // Open payment link in new window
          window.open(response.data.paymentLink, '_blank');

          this.paymentStatus = {
            type: 'success',
            message: 'Payment link created successfully. Complete your payment in the new window.',
          };
        } else {
          throw new Error('Failed to create payment link');
        }
      } catch (error) {
        // console.error('Pay by link error:', error);
        this.paymentError = 'Failed to create payment link. Please try again.';
      } finally {
        this.processingPayByLink = false;
      }
    },

    /* eslint-disable sonarjs/no-same-line-conditional */
    validatePhoneNumber(phoneNumber) {
      const cleanedNumber = phoneNumber.replace(/[\s\-()]/g, '');

      if (cleanedNumber.startsWith('0')) {
        return `254${cleanedNumber.substring(1)}`;
      } if (cleanedNumber.startsWith('+254')) {
        return cleanedNumber.substring(1);
      } if (cleanedNumber.startsWith('254')) {
        return cleanedNumber;
      }

      if (cleanedNumber.length === 9 && cleanedNumber.startsWith('7')) {
        return `254${cleanedNumber}`;
      }

      return cleanedNumber.match(/^254[17]\d{8}$/) ? cleanedNumber : null;
    },
    /* eslint-enable sonarjs/no-same-line-conditional */

    async pollPaymentStatus(paymentId) {
      if (!paymentId) return;

      const maxAttempts = 30; // 5 minutes of polling
      let attempts = 0;

      const poll = async () => {
        if (attempts >= maxAttempts) {
          this.paymentStatus = {
            type: 'warning',
            message: 'Payment verification timed out. Please check your M-Pesa messages and contact support if needed.',
          };
          return;
        }

        try {
          const headers = await nonResidentAuth.getAuthHeaders();

          const response = await axios.get(
            'https://prod.api.pacisinsurance.com/customer/payment/client-api/v1/payments/mpesa/payment/query',
            {
              params: { paymentId },
              headers,
            },
          );

          const status = response.data?.data?.status;

          if (status === 'COMPLETED') {
            this.paymentSuccess = true;
            this.paymentStatus = {
              type: 'success',
              message: 'Payment completed successfully! Your travel insurance policy is being processed.',
            };

            // Issue the travel policy using the same service as residents
            await this.issueTravelPolicy();
          } else if (status === 'FAILED' || status === 'CANCELLED') {
            this.paymentStatus = {
              type: 'error',
              message: 'Payment was cancelled or failed. Please try again.',
            };
          } else {
            // Continue polling
            attempts += 1;
            setTimeout(poll, 10000); // Poll every 10 seconds
          }
        } catch (error) {
          // console.error('Payment status check error:', error);
          attempts += 1;
          setTimeout(poll, 10000);
        }
      };

      // Start polling after 5 seconds
      setTimeout(poll, 5000);
    },

    async issueTravelPolicy() {
      try {
        // console.log('Issuing travel policy for non-resident...');

        // Create payment context similar to resident flow
        const paymentContext = {
          productId: this.quote.id,
          payableAmount: this.kshAmount,
          email: this.formData.email,
          phoneNumber: this.formData.mobile_main,
          transactionReference: travelPaymentReference(this.form?.passport_number || this.form?.passport_no || this.quote?.passport_no),
          transactionDescription: `TV_rfq-${this.quote.id}`,
          serviceType: 'TRAVEL',
          transactionType: 'PAYMENT',
        };

        // console.log('Non-resident payment context:', paymentContext);

        // Issue the policy using the same service as residents
        const result = await TravelPolicyService.issueTravelPolicy(paymentContext, this.$store);

        // Update status to show policy issued successfully
        this.paymentStatus = {
          type: 'success',
          message: `${result.message} Policy details have been sent to your email.`,
        };

        // console.log('Travel policy issued successfully:', result);

        // Redirect after showing success message
        setTimeout(() => {
          this.$router.push({ name: 'Dashboard' });
        }, 5000);
      } catch (error) {
        // console.error('Failed to issue travel policy:', error);
        this.paymentStatus = {
          type: 'error',
          message: 'Payment successful but policy issuance failed. Please contact support for assistance.',
        };
      }
    },
  },
};
</script>

<style scoped>
.payment-section {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 24px;
  margin-top: 16px;
}

.text-h4.primary--text {
  font-weight: bold;
}

.v-card {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.headline {
  background: linear-gradient(135deg, #1976d2 0%, #42a5f5 100%);
  color: white;
}
</style>
